import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

export default function ControllerPage() {
  const { user, logout } = useAuth();
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [loading, setLoading] = useState(false);
  const html5QrCodeRef = useRef(null);

  const processQRCode = async (qrCode) => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await api.post('/tickets/scan', { qr_code: qrCode });
      setScanResult(res.data);
    } catch (err) {
      const errData = err.response && err.response.data ? err.response.data : null;
      setScanResult(errData || { valid: false, result: 'error', message: 'Erreur de connexion' });
    } finally {
      setLoading(false);
    }
  };

  const startScanner = async () => {
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const html5QrCode = new Html5Qrcode('qr-reader');
      html5QrCodeRef.current = html5QrCode;
      await html5QrCode.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        async (decodedText) => {
          if (!loading) {
            await html5QrCode.stop();
            setScanning(false);
            await processQRCode(decodedText);
          }
        },
        () => {}
      );
      setScanning(true);
    } catch (err) {
      alert('Impossible d acces a la camera. Utilisez la saisie manuelle.');
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try { await html5QrCodeRef.current.stop(); } catch {}
    }
    setScanning(false);
  };

  const resetScan = async () => {
    setScanResult(null);
    setManualCode('');
    if (scanning) await stopScanner();
  };

  const handleManualScan = async () => {
    if (!manualCode.trim()) return;
    await processQRCode(manualCode.trim());
  };

  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        try { html5QrCodeRef.current.stop(); } catch {}
      }
    };
  }, []);

  const resultConfig = {
    valid: { icon: '✅', label: 'Billet valide !', cls: 'valid', action: 'Entree autorisee' },
    already_used: { icon: '⚠️', label: 'Deja utilise', cls: 'already_used', action: 'Entree refusee' },
    not_found: { icon: '❌', label: 'Billet introuvable', cls: 'invalid', action: 'Entree refusee' },
    invalid: { icon: '❌', label: 'Billet invalide', cls: 'invalid', action: 'Entree refusee' },
    error: { icon: '&#128308;', label: 'Erreur syst&egrave;me', cls: 'invalid', action: 'R&eacute;essayez' },
  };

  const cfg = scanResult ? (resultConfig[scanResult.result] || resultConfig.error) : null;

  return (
    <div className="app-layout">
      <aside className="sidebar">
        <div className="sidebar-logo">
          <div className="logo-mark">
            <div className="logo-icon">&#127915;</div>
            <div><div className="logo-text">ComoTicket</div><div className="logo-sub">Controle acces</div></div>
          </div>
        </div>
        <nav className="sidebar-nav">
          <div className="nav-section">
            <button className="nav-item active"><span className="nav-icon">&#128247;</span>Scanner un billet</button>
          </div>
        </nav>
        <div className="sidebar-footer">
          <div className="user-card">
            <div className="avatar" style={{ background: 'var(--amber)' }}>{user && user.name ? user.name[0].toUpperCase() : 'C'}</div>
            <div className="user-info">
              <div className="user-name">{user && user.name}</div>
              <div className="user-role">Controleur</div>
            </div>
            <button className="logout-btn" onClick={logout}>&#8618;</button>
          </div>
        </div>
      </aside>

      <main className="main-content">
        <div className="page-header">
          <div className="page-title">Contr&ocirc;le d'entr&eacute;e</div>
          <div className="page-subtitle">Scanner les QR codes des billets</div>
        </div>
        <div className="page-body">
          <div className="scanner-container">
            {!scanResult ? (
              <div className="card">
                <div className="card-header"><div className="card-title">Scanner un billet</div></div>
                <div className="scanner-box" style={{ marginBottom: 20 }}>
                  <div id="qr-reader" style={{ width: '100%' }} />
                  {!scanning && (
                    <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.7)', padding: 40 }}>
                      <div style={{ fontSize: 64, marginBottom: 16 }}>&#128247;</div>
                      <div style={{ fontSize: 16, fontWeight: 600 }}>Camera inactive</div>
                      <div style={{ fontSize: 13, marginTop: 6, color: 'rgba(255,255,255,0.45)' }}>Cliquez sur Demarrer</div>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                  {!scanning ? (
                    <button className="btn btn-primary btn-lg btn-full" onClick={startScanner} disabled={loading}>
                      &#128247; Demarrer le scan
                    </button>
                  ) : (
                    <button className="btn btn-danger btn-lg btn-full" onClick={stopScanner}>
                      Arreter
                    </button>
                  )}
                </div>
                <div style={{ borderTop: '1px solid var(--gray-200)', paddingTop: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-600)', marginBottom: 10 }}>Ou saisir le code manuellement</div>
                  <div className="manual-input-row">
                    <input className="form-input" placeholder="Entrer le code QR..." value={manualCode} onChange={(e) => setManualCode(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleManualScan()} />
                    <button className="btn btn-primary" onClick={handleManualScan} disabled={loading || !manualCode.trim()}>
                      {loading ? '...' : 'Verifier'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className={`scan-result ${cfg.cls}`}>
                  <div className="scan-icon" dangerouslySetInnerHTML={{__html: cfg.icon}} />
                  <div className={`scan-title ${cfg.cls}`} dangerouslySetInnerHTML={{__html: cfg.label}} />
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }} dangerouslySetInnerHTML={{__html: cfg.action}} />
                  {scanResult.message && <div style={{ fontSize: 14, color: 'var(--gray-600)', marginBottom: 12 }}>{scanResult.message}</div>}
                  {scanResult.ticket && (
                    <div className="scan-info" style={{ textAlign: 'left', background: 'white', borderRadius: 8, padding: 16, marginTop: 12 }}>
                      <div>&#127914; <strong>{scanResult.ticket.event_name}</strong></div>
                      {scanResult.ticket.location && <div>&#128205; <strong>{scanResult.ticket.location}</strong></div>}
                      {scanResult.ticket.holder_name && <div>&#128100; <strong>{scanResult.ticket.holder_name}</strong></div>}
                      {scanResult.ticket.sale_reference && <div>&#128273; Ref: <strong>#{scanResult.ticket.sale_reference}</strong></div>}
                    </div>
                  )}
                </div>
                <button className="btn btn-ghost btn-lg btn-full" style={{ marginTop: 16 }} onClick={resetScan}>
                  Scanner un autre billet
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}